import { useEffect, useRef, useState } from 'react'
import { useClientAuthStore } from '../../store/clientAuthStore.js'
import { User, Bot, X, Smile, Send, Trash2, ChevronRight, Lock, Phone } from 'lucide-react'
import { type ChatMessage, historyKey, loadHistory, saveHistory, loadEscalationFlag, saveEscalationFlag, loadSessionId, saveSessionId, clearSessionId } from '../../utils/clientAiChatStorage.js'

interface ClientAiChatProps {
  tenantSlug: string
  onClose: () => void
  onTalkToHuman: () => void
  callEnabled: boolean
  onCallSupport: () => void
}

function MessageBubble({ role, content, time, isTyping, aiBadge }: { role: 'user' | 'assistant'; content?: string; time?: string; isTyping?: boolean; aiBadge?: boolean }) {
  const isUser = role === 'user'
  const displayTime = time ?? new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  return (
    <div className={`flex w-full items-end gap-2 ${isUser ? 'flex-row-reverse' : ''}`}>
      {!isUser && (
        <div className="relative h-8 w-8 shrink-0 self-start">
          <div className="h-8 w-8 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center">
            <Bot className="h-4 w-4" />
          </div>
          <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-green-500 border-2 border-white" />
        </div>
      )}
      <div className={`max-w-[85%] flex flex-col ${isUser ? 'items-end' : 'items-start'}`}>
        {!isUser && aiBadge && (
          <span className="mb-1 px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-[11px] font-semibold">AI assistant</span>
        )}
        <div className={`px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap break-words max-w-full w-max ${isUser ? 'bg-blue-600 text-white rounded-2xl rounded-tr-none' : 'bg-purple-50 text-gray-800 rounded-2xl rounded-tl-none'}`}>
          {isTyping ? (
            <span className="inline-flex items-center gap-1">
              <span className="h-2 w-2 rounded-full bg-gray-500 animate-bounce [animation-delay:-0.3s]" />
              <span className="h-2 w-2 rounded-full bg-gray-500 animate-bounce [animation-delay:-0.15s]" />
              <span className="h-2 w-2 rounded-full bg-gray-500 animate-bounce" />
            </span>
          ) : (
            content
          )}
        </div>
        <span className="text-[10px] text-gray-400 mt-1 px-1">{displayTime}</span>
      </div>
    </div>
  )
}

const AGENT_REQUEST_PATTERNS: RegExp[] = [
  /\b(talk|speak|chat)\s+(to|with)\s+(a|an)?\s*(agent|human|person|representative|support)\b/i,
  /\b(live\s+agent|real\s+person|human\s+support|call\s+support)\b/i,
  /\bconnect\s+(me\s+)?(to|with)\s+(a|an)?\s*(agent|support)\b/i,
  /\b(i\s+want|can\s+i\s+(get|speak|talk|have)|need)\s+(a|an)?\s*(agent|human|representative|support)\b/i,
]

function wantsLiveAgent(text: string): boolean {
  return AGENT_REQUEST_PATTERNS.some((pattern) => pattern.test(text))
}

export function ClientAiChat({ tenantSlug, onClose, onTalkToHuman, callEnabled, onCallSupport }: ClientAiChatProps) {
  const [messages, setMessages] = useState<ChatMessage[]>(() => loadHistory(tenantSlug))
  const [input, setInput] = useState('')
  const [pending, setPending] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [escalationOffered, setEscalationOffered] = useState(() => loadEscalationFlag(tenantSlug))
  const sessionIdRef = useRef<string | null>(loadSessionId(tenantSlug))
  const user = useClientAuthStore((s) => s.user)
  const previousUserRef = useRef(user)
  const abortRef = useRef<AbortController | null>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const isClearChatVisible = false
  // AG-05: tenant setting (default ON) - "AI assistant" badge + one-line notice.
  const [disclosure, setDisclosure] = useState<{ enabled: boolean; text: string } | null>(null)

  useEffect(() => {
    let cancelled = false
    fetch(`/api/client-portal/${encodeURIComponent(tenantSlug)}/ai/disclosure`, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { enabled?: boolean; text?: string } | null) => {
        if (!cancelled && data && data.enabled === true && typeof data.text === 'string' && data.text) {
          setDisclosure({ enabled: true, text: data.text })
        }
      })
      .catch(() => {
        // disclosure is best-effort; the chat itself must keep working
      })
    return () => {
      cancelled = true
    }
  }, [tenantSlug])
  const aiBadge = disclosure !== null

  useEffect(() => {
    saveHistory(tenantSlug, messages)
  }, [tenantSlug, messages])

  useEffect(() => {
    if (previousUserRef.current && !user) {
      setMessages([])
      setPending('')
      setError(null)
      sessionIdRef.current = null
      try {
        localStorage.removeItem(historyKey(tenantSlug))
        clearSessionId(tenantSlug)
      } catch {
        // ignore
      }
    }
    previousUserRef.current = user
  }, [user, tenantSlug])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, pending, error])

  useEffect(() => {
    return () => {
      abortRef.current?.abort()
    }
  }, [])

  const streamChat = async (msgs: ChatMessage[]) => {
    const sanitized = msgs.filter((m) => m.content?.trim())
    if (sanitized.length === 0) {
      setIsLoading(false)
      return
    }
    // Trim to the most recent 30 messages to stay within the API limit
    // and avoid sending unbounded history as the conversation grows.
    const trimmed = sanitized.slice(-30)
    try {
      abortRef.current?.abort()
      abortRef.current = new AbortController()
      const res = await fetch(`/api/client-portal/${encodeURIComponent(tenantSlug)}/ai/chat/stream`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        signal: abortRef.current.signal,
        body: JSON.stringify({ messages: trimmed, ...(sessionIdRef.current ? { sessionId: sessionIdRef.current } : {}) }),
      })

      if (!res.ok) {
        const text = await res.text()
        throw new Error(`AI chat failed: ${res.status} ${text}`)
      }

      const body = res.body
      if (!body) throw new Error('No response body')

      const reader = body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      for (;;) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })

        const blocks = buffer.split('\n\n')
        buffer = blocks.pop() ?? ''

        for (const block of blocks) {
          const lines = block.split('\n')
          let event = ''
          let data = ''
          for (const line of lines) {
            if (line.startsWith('event:')) {
              event = line.slice(6).trim()
            } else if (line.startsWith('data:')) {
              data = line.slice(5).trim()
            }
          }
          if (!event || !data) continue
          try {
            const payload = JSON.parse(data) as unknown
            if (event === 'meta') {
              const metaPayload = payload as { sessionId?: string }
              if (typeof metaPayload.sessionId === 'string' && metaPayload.sessionId) {
                sessionIdRef.current = metaPayload.sessionId
                saveSessionId(tenantSlug, metaPayload.sessionId)
              }
            } else if (event === 'done') {
              const donePayload = payload as { content?: string; escalate?: boolean }
              const doneContent = (donePayload.content ?? '').trim()
              if (doneContent) {
                setMessages((prev) => [...prev, { role: 'assistant', content: doneContent }])
              }
              setPending('')
              setIsLoading(false)
              if (donePayload.escalate) {
                setEscalationOffered(true)
                saveEscalationFlag(tenantSlug, true)
              }
            } else if (event === 'error') {
              throw new Error(String((payload as { error?: string }).error ?? 'AI stream error'))
            }
          } catch (err) {
            throw err instanceof Error ? err : new Error(String(err))
          }
        }
      }

      setIsLoading(false)
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        setPending('')
      } else {
        setError(err instanceof Error ? err.message : 'Something went wrong')
      }
      setIsLoading(false)
    }
  }

  const handleSend = async () => {
    const text = input.trim()
    if (!text || isLoading) return
    const nextMessages: ChatMessage[] = [...messages, { role: 'user', content: text }]
    setMessages(nextMessages)
    setInput('')
    setIsLoading(true)
    setPending('')
    setError(null)

    if (wantsLiveAgent(text)) {
      setEscalationOffered(true)
      saveEscalationFlag(tenantSlug, true)
    }

    await streamChat(nextMessages)
  }

  const handleRetry = async () => {
    if (isLoading || messages.length === 0) return
    setIsLoading(true)
    setPending('')
    setError(null)
    await streamChat(messages)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const clearHistory = () => {
    setMessages([])
    setPending('')
    setError(null)
    setEscalationOffered(false)
    sessionIdRef.current = null
    try {
      localStorage.removeItem(historyKey(tenantSlug))
      saveEscalationFlag(tenantSlug, false)
      clearSessionId(tenantSlug)
    } catch {
      // ignore
    }
  }

  return (
    <div className="kc3-widget-panel">
      <div className="kc3-widget h-full overflow-hidden flex flex-col bg-white">
        <div className="flex items-center justify-between px-5 py-5 bg-gradient-to-br from-blue-600 to-indigo-600 text-white flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
              <Bot className="h-6 w-6" />
            </div>
            <div>
              <p className="font-semibold text-base leading-tight">VozPilot</p>
              <p className="text-sm text-white/80">
                Intelligent assistant 24/7
                <span className="inline-block align-middle h-1.5 w-1.5 rounded-full bg-green-400 mx-1.5" />
                Online
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} aria-label="Close chat" className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 bg-white">
          <div className="flex items-center gap-3 my-4 text-xs text-gray-400">
            <div className="h-px flex-1 bg-gray-100" />
            <span>Today</span>
            <div className="h-px flex-1 bg-gray-100" />
          </div>

          {disclosure && (
            <p className="mb-3 text-center text-xs text-gray-500" data-testid="ai-disclosure-notice">{disclosure.text}</p>
          )}

          <div className="space-y-4">
            {messages.length === 0 && !pending && (
              <MessageBubble
                aiBadge={aiBadge}
                role="assistant"
                content={"Hi there! 👋\nI can help answer questions about your account and services.\nWhat would you like to know?"}
              />
            )}
            {messages.map((m, idx) => (
              <MessageBubble key={idx} role={m.role} content={m.content} aiBadge={aiBadge} />
            ))}
            {pending && <MessageBubble role="assistant" content={pending} aiBadge={aiBadge} />}
            {isLoading && !pending && <MessageBubble role="assistant" isTyping aiBadge={aiBadge} />}
            {error && (
              <div className="flex items-start gap-2">
                <div className="rounded-xl bg-red-50 p-3 text-sm text-red-700 flex-1">
                  <p>{error}</p>
                  <button
                    type="button"
                    onClick={handleRetry}
                    disabled={isLoading}
                    className="mt-2 px-3 py-1.5 bg-white border border-red-200 rounded-lg text-red-700 text-xs font-medium hover:bg-red-100 disabled:opacity-50"
                  >
                    Retry
                  </button>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
        </div>

        <div className="px-4 pt-3 pb-2 bg-white border-t border-gray-100">
          {escalationOffered && (
            <div className="grid grid-cols-2 gap-3 mb-3">
              <button
                type="button"
                onClick={() => {
                  console.log('[kc3-client-portal] "Live Agent" option clicked by user — initiating live agent escalation')
                  onTalkToHuman()
                }}
                className="flex items-center gap-3 p-3 rounded-xl border border-blue-100 bg-white shadow-sm hover:shadow-md transition text-left"
              >
                <div className="h-10 w-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0">
                  <User className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-blue-700">Live Agent</p>
                  <p className="text-xs text-gray-500">Human support</p>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
              </button>

              {callEnabled && (
                <button
                  type="button"
                  onClick={onCallSupport}
                  className="flex items-center gap-3 p-3 rounded-xl border border-green-100 bg-white shadow-sm hover:shadow-md transition text-left"
                >
                  <div className="h-10 w-10 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0">
                    <Phone className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-green-700">Call Support</p>
                    <p className="text-xs text-gray-500">Speak with an agent</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                </button>
              )}
            </div>
          )}

          {isClearChatVisible && (<button
            type="button"
            onClick={clearHistory}
            disabled={messages.length === 0}
            className="w-full flex items-center gap-3 p-3 rounded-xl border border-red-100 bg-white shadow-sm hover:shadow-md transition text-left disabled:opacity-50"
          >
            <div className="h-10 w-10 rounded-full bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0">
              <Trash2 className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm text-red-700">Clear chat</p>
              <p className="text-xs text-gray-500">Start a new conversation</p>
            </div>
            <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
          </button>)}
        </div>

        <div className="p-4 bg-white">
          <div className="flex items-end gap-2">
            <div className="flex-1 flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-2xl bg-white focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition">
              <textarea
                className="flex-1 resize-none bg-transparent outline-none text-sm text-gray-800 placeholder:text-gray-400 max-h-24"
                rows={1}
                aria-label="Type your question"
                placeholder="Type your question..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
              />
              <button type="button" aria-label="Add emoji" className="text-gray-400 hover:text-gray-600">
                <Smile className="h-5 w-5" />
              </button>
            </div>
            <button
              type="button"
              onClick={handleSend}
              disabled={!input.trim() || isLoading}
              className="px-5 py-2.5 bg-blue-600 text-white rounded-2xl text-sm font-medium flex items-center gap-2 hover:bg-blue-700 disabled:opacity-50 transition"
            >
              <Send className="h-4 w-4" />
              Send
            </button>
          </div>
        </div>

        <div className="pb-3 pt-1 flex items-center justify-center gap-1.5 text-[10px] text-gray-400 bg-white">
          <Lock className="h-3 w-3" />
          <span>
            Your data is secure and private &bull;{' '}
            <a
              href="/client-portal/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-gray-700 underline"
            >
              Privacy Notice
            </a>
          </span>
        </div>
      </div>
    </div>
  )
}
